import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, lazyRouteComponent, useNavigate, useRouterState } from '@tanstack/react-router'
import { Command, useCommandState } from 'cmdk'
import { FilePlus2, NotebookPen, Sparkles } from 'lucide-react'
import { cn, trpc } from './anschluss'
import { FRAG, NEUES_BLATT, PFAD } from './marken'
import { useEntwuerfeNachreichen } from './entwuerfe'

/**
 * **Das Notizbuch – der einzige Eingang für Work** ([Z-23](../../../../docs/phasen/zusatz-23-notizbuch.md),
 * [E-419](../../../../docs/ENTSCHEIDUNGEN.md#e-419)).
 *
 * Diese Datei bleibt klein, weil sie im Hauptbündel landet: Work importiert von hier das Häkchen
 * für die Benutzerverwaltung und den Verweis auf die Ansicht. Die Ansicht selbst (`seite.tsx`)
 * und alles, was später dazukommt – Editor, Suche – wird erst beim Öffnen von `/notizbuch`
 * geladen (Leitplanke 4).
 */
export const NotizbuchSeite = lazyRouteComponent(() => import('./seite'), 'Seite')

/** „Festhalten" in My Work (Z-24) legt Notizen über diesen Hook ab – klein, ohne Editor. */
export { useNotizAblage, type AblageErgebnis } from './festhalten'

const FREIGABEN = ['notizbuch', 'freigaben'] as const

const STAND = {
  queryKey: ['notizbuch', 'stand'],
  queryFn: () => trpc.notizbuch.stand.query(),
  staleTime: 5 * 60_000,
}

/**
 * **Das Notizbuch in ⌘K** – „Neues Blatt" und „Notizbuch öffnen" (Leitplanke 3: alles per
 * Tastatur). Nur für wen das Häkchen gesetzt ist.
 */
export function NotizbuchBefehle({
  zu,
  gruppe,
  zeile,
}: {
  zu: () => void
  gruppe: string
  zeile: string
}) {
  const navigate = useNavigate()
  const { data: stand } = useQuery(STAND)
  // Was in ⌘K steht – ab drei Zeichen: „✦ Frag dein Notizbuch: …"
  // „? frag …" (Konzept): das Fragezeichen sagt nur „ich will fragen" – es gehört nicht zur Frage
  const roh = useCommandState((z) => z.search).trim()
  const suche = roh.replace(/^\?\s*(frag(e)?\s+)?/i, '').trim()
  const fragtAusdruecklich = roh.startsWith('?')
  if (!stand?.freigegeben) return null
  return (
    <Command.Group heading="Notizbuch" className={gruppe}>
      {suche.length >= 3 && (
        <Command.Item
          // Mit „?" vorne immer sichtbar und oben – unabhängig davon, was cmdk sonst findet
          value={`notizbuch:frag ${roh}`}
          {...(fragtAusdruecklich ? { forceMount: true } : {})}
          onSelect={() => {
            zu()
            void navigate({ to: PFAD, hash: FRAG + encodeURIComponent(suche) })
          }}
          className={zeile}
        >
          <Sparkles className="size-[17px] opacity-75" />
          Frag dein Notizbuch: „{suche}"
        </Command.Item>
      )}
      <Command.Item
        value="notizbuch:neu Neues Blatt Notiz schreiben"
        onSelect={() => {
          zu()
          void navigate({ to: PFAD, hash: NEUES_BLATT })
        }}
        className={zeile}
      >
        <FilePlus2 className="size-[17px] opacity-75" />
        Neues Blatt
      </Command.Item>
      <Command.Item
        value="notizbuch:oeffnen Notizbuch öffnen"
        onSelect={() => {
          zu()
          void navigate({ to: PFAD })
        }}
        className={zeile}
      >
        <NotebookPen className="size-[17px] opacity-75" />
        Notizbuch öffnen
      </Command.Item>
    </Command.Group>
  )
}

/**
 * **„Notizbuch" in der Seitenleiste** – nur für wen das Häkchen gesetzt ist. Sieht aus wie die
 * Punkte daneben, gehört aber dem Notizbuch: die Navigation von Work kennt es nicht.
 */
export function NotizbuchNavEintrag({ schmal = false }: { schmal?: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { data: stand } = useQuery(STAND)
  // Steht immer in der Seitenleiste – darum reicht Work von hier offline Geschriebenes nach
  useEntwuerfeNachreichen(!!stand?.freigegeben)
  if (!stand?.freigegeben) return null
  const aktiv = pathname === PFAD
  if (schmal) {
    return (
      <Link
        to={PFAD}
        aria-label="Notizbuch"
        title="Notizbuch"
        className={cn(
          'flex h-10 w-full items-center justify-center rounded-[10px] transition-colors',
          aktiv ? 'bg-accent-soft text-accent-deep' : 'text-ink2 hover:bg-[#f3f2ef]',
        )}
      >
        <NotebookPen className={cn('size-[18px]', aktiv ? 'opacity-100' : 'opacity-75')} />
      </Link>
    )
  }
  return (
    <Link
      to={PFAD}
      className={cn(
        'flex w-full items-center gap-[11px] rounded-[10px] px-3 py-[9px] text-left text-[13.5px] font-medium transition-colors',
        aktiv ? 'bg-accent-soft font-semibold text-accent-deep' : 'text-ink2 hover:bg-[#f3f2ef]',
      )}
    >
      <NotebookPen className={cn('size-[17px] shrink-0', aktiv ? 'opacity-100' : 'opacity-75')} />
      Notizbuch
    </Link>
  )
}

/**
 * **„📒 Notizbuch" in der Benutzerverwaltung** – wie „🤖 Claude": wer das Häkchen hat, kann das
 * Notizbuch benutzen ([E-422](../../../../docs/ENTSCHEIDUNGEN.md#e-422)). Nur für die
 * Administration sichtbar, weil nur sie die Benutzerverwaltung sieht. Abnehmen löscht nichts.
 */
export function NotizbuchHaekchen({ personId }: { personId: string }) {
  const queryClient = useQueryClient()
  const { data: freigaben } = useQuery({
    queryKey: FREIGABEN,
    queryFn: () => trpc.notizbuch.freigaben.query(),
  })
  // Optimistisch (Leitplanke 1): das Häkchen springt sofort, bei einem Fehler zurück.
  const setzen = useMutation({
    mutationFn: (an: boolean) => trpc.notizbuch.freigabeSetzen.mutate({ personId, an }),
    onMutate: async (an) => {
      await queryClient.cancelQueries({ queryKey: FREIGABEN })
      const vorher = queryClient.getQueryData<string[]>(FREIGABEN)
      queryClient.setQueryData<string[]>(FREIGABEN, (alt = []) =>
        an ? [...new Set([...alt, personId])] : alt.filter((id) => id !== personId),
      )
      return { vorher }
    },
    onError: (_fehler, _an, kontext) => queryClient.setQueryData(FREIGABEN, kontext?.vorher),
    onSettled: () => queryClient.invalidateQueries({ queryKey: FREIGABEN }),
  })
  if (!freigaben) return null
  return (
    <label className="mt-1 flex cursor-pointer items-center gap-1.5 text-[11px] text-ink2">
      <input
        type="checkbox"
        checked={freigaben.includes(personId)}
        onChange={(e) => setzen.mutate(e.target.checked)}
        className="cursor-pointer"
      />
      📒 Notizbuch
    </label>
  )
}
